/**
 * NavigationWrapper.tsx - Navigatr-based navigation for FMwvTool
 * 
 * CHANGE HISTORY:
 * - 251115_2320 [pending] danielbeer: Show ALL features and plugins with typeahead search
 * 
 * REPLACES: src/components/Navigation.tsx (custom implementation)
 * 
 * FEATURES:
 * - NavigationBar (top bar with back/forward/breadcrumbs)
 * - SideMenu with ALL plugins and features
 * - Grouped by category (Documents, Data, UI, FileMaker, etc.)
 * - Typeahead search filter by name
 * - Recent views tracking
 * - Persistent storage
 */

import { useNavigatr, NavigationBar, SideMenu } from '@opcentral/dependr';
import React, { useState, useEffect, useMemo } from 'react';
import type { MenuItemType } from '@opcentral/dependr';
import { Panel } from '@opcentral/dependr';
import { usePanelr, usePanel } from '@opcentral/dependr';
import { pluginRegistry } from '../pluginRegistry';
import { VERSION, BUILD_TIMESTAMP } from '../version';
import type { ToolType } from '../types';
// Navigation styles now provided by Navigatr package

interface NavigationWrapperProps {
  currentTool: ToolType;
  onToolChange: (tool: ToolType) => void;
  actionButtons?: React.ReactNode;
  onToggleNavVersion?: () => void;
  useV2Navigation?: boolean;
}

// Category icons and labels
const CATEGORY_CONFIG: Record<string, { label: string; icon: string }> = {
  document: { label: 'Documents', icon: '📄' },
  data: { label: 'Data Processing', icon: '🔄' },
  ui: { label: 'UI Components', icon: '📊' },
  integration: { label: 'FileMaker Integration', icon: '🔗' },
  utility: { label: 'Utilities', icon: '🛠️' },
  examples: { label: 'Examples & Demos', icon: '✨' },
  'filemaker-scripts': { label: 'FileMaker Scripts', icon: '📜' },
  system: { label: 'System', icon: '⚙️' }
};

// Memoize widths object outside component to prevent re-creation
const SIDEBAR_WIDTHS = { minimized: 60, normal: 280 };

export const NavigationWrapper: React.FC<NavigationWrapperProps> = ({ currentTool, onToolChange, actionButtons, onToggleNavVersion, useV2Navigation = false }) => {
  const { setView } = useNavigatr('fmwvtool', {
    expirationTime: 24 * 60 * 60 * 1000, // 24 hours
    maxRecentItems: 10
  });
  const { panelr, getState } = usePanelr();
  
  // Get sidebar state without re-registering (to avoid overwriting widths config)
  const [sidebarMode, setSidebarMode] = React.useState(() => getState('fmwvtool-sidebar')?.mode || 'normal');
  
  // Subscribe to panel state changes
  React.useEffect(() => {
    const unsubscribe = panelr.on('state-changed', ({ id, state }) => {
      if (id === 'fmwvtool-sidebar') {
        // console.log('[NavigationWrapper] Sidebar state changed:', state.mode); // Disabled - too noisy
        setSidebarMode(state.mode);
      }
    });
    return unsubscribe;
  }, [panelr]);
  
  const isMinimized = sidebarMode === 'minimized';
  // console.log('[NavigationWrapper] Sidebar mode:', sidebarMode, 'isMinimized:', isMinimized); // Disabled - too noisy

  const handleNavigate = (toolId: string) => {
    console.log('[NavigationWrapper] handleNavigate called with:', toolId);
    
    // Update Navigatr state
    setView('tool', { toolId });
    
    // Notify parent component
    console.log('[NavigationWrapper] Calling onToolChange with:', toolId);
    onToolChange(toolId as ToolType);
  };

  // Group plugins by category
  const pluginsByCategory = pluginRegistry.reduce((acc, plugin) => {
    const category = plugin.category || 'utility';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(plugin);
    return acc;
  }, {} as Record<string, typeof pluginRegistry>);

  // Convert to MenuItem format with categories as submenus
  // console.log('[NavigationWrapper] Creating menu items with currentTool:', currentTool); // Disabled - too noisy
  const menuItems: MenuItemType[] = [
    // Home
    {
      id: 'home',
      type: 'action',
      label: 'Home',
      icon: '🏠',
      action: () => handleNavigate('home'),
      selected: currentTool === 'home'
    },
    
    // Separator
    {
      id: 'sep-home',
      type: 'separator',
      label: ''
    },
    
    // Create submenu for each category
    ...Object.entries(pluginsByCategory).map(([category, plugins]) => {
      const config = CATEGORY_CONFIG[category] || { label: category, icon: '📦' };
      
      return {
        id: `category-${category}`,
        type: 'submenu' as const,
        label: config.label,
        icon: config.icon,
        badge: plugins.length,
        expanded: true, // Start expanded
        children: plugins
          .sort((a, b) => a.name.localeCompare(b.name)) // Alphabetical
          .map(plugin => ({
            id: plugin.id,
            type: 'action' as const,
            label: plugin.name,
            icon: getPluginIcon(plugin),
            tooltip: plugin.description,
            badge: plugin.enabled ? undefined : 'disabled',
            disabled: !plugin.enabled,
            selected: currentTool === plugin.id,
            action: () => handleNavigate(plugin.id),
            metadata: {
              version: plugin.version,
              tags: plugin.tags,
              source: plugin.source
            }
          }))
      };
    }),
    
    // Separator
    {
      id: 'sep-system',
      type: 'separator',
      label: ''
    },
    
    // System items (Settings moved to top nav)
    {
      id: 'dev-panel',
      type: 'action',
      label: 'Dev Panel',
      icon: '🛠️',
      action: () => handleNavigate('dev'),
      selected: currentTool === 'dev'
    }
  ];

  const handleMenuItemClick = (item: MenuItemType) => {
    if (item.action) {
      item.action();
    }
  };

  // Toggle button handler - cycle through modes
  const handleToggle = () => {
    panelr.cycleMode('fmwvtool-sidebar');
  };

  // Return top nav and sidebar separately so they can be positioned correctly
  const topNav = (
    <Panel
        id="fmwvtool-topbar"
        position="top"
        defaultWidth={40}
        resizable={false}
        collapsible={false}
        persistent={false}
      >
        <div className="nav-bar-container">
          <button 
            className="nav-toggle-button"
            onClick={handleToggle}
            aria-label="Toggle navigation menu"
          >
            ☰
          </button>
          {onToggleNavVersion && (
            <button 
              className={`nav-toggle-button nav-version-toggle ${useV2Navigation ? 'active' : ''}`}
              onClick={onToggleNavVersion}
              title={`Switch to ${useV2Navigation ? 'Classic' : 'V2'} Navigation`}
              style={{ 
                marginLeft: '4px',
                background: useV2Navigation ? 'var(--accent-primary, #007acc)' : 'transparent',
                color: useV2Navigation ? '#fff' : 'inherit'
              }}
            >
              {useV2Navigation ? '🆕' : '📋'}
            </button>
          )}
          <NavigationBar
            appName="fmwvtool"
            showBackForward={true}
            showBreadcrumbs={true}
            showHistory={false}
            className="nav-bar"
          />
          {actionButtons && (
            <div className="nav-action-buttons">
              {actionButtons}
            </div>
          )}
          <div className="nav-version-topbar" title={`Build: ${BUILD_TIMESTAMP}`}>
            <span className="version-number">v{VERSION}</span>
            <span className="version-date">{BUILD_TIMESTAMP}</span>
          </div>
        </div>
      </Panel>
  );
  
  return topNav;
}

// Export sidebar separately
export const NavigationSidebar: React.FC<{ currentTool: ToolType; onToolChange: (tool: ToolType) => void }> = ({ currentTool, onToolChange }) => {
  const { setView } = useNavigatr();
  const [showFavoritesOnly, setShowFavoritesOnly] = React.useState(false);
  
  // Function to inject stars
  const injectStars = React.useCallback(() => {
    console.log('[NavigationSidebar] Injecting star buttons...');
    
    // Find all menu item buttons with our class
    const buttons = document.querySelectorAll('.menu-item.menu-item-with-star');
    console.log('[NavigationSidebar] Found buttons:', buttons.length);
    
    buttons.forEach((button) => {
      // Check if star already exists
      if (button.querySelector('.fav-star-container')) {
        return;
      }
      
      const pluginId = button.getAttribute('aria-label');
      if (!pluginId) return;
      
      // Create star container
      const starContainer = document.createElement('span');
      starContainer.className = 'fav-star-container';
      starContainer.innerHTML = '☆';
      starContainer.style.cssText = 'margin-left: auto; cursor: pointer; opacity: 0.4; font-size: 1rem; padding: 0 0.25rem;';
      
      // Check if favorited
      const favorites = JSON.parse(localStorage.getItem('favr_favorites') || '{}');
      const isFavorited = favorites['fmwvtool-plugins']?.[pluginId];
      if (isFavorited) {
        starContainer.innerHTML = '★';
        starContainer.style.opacity = '1';
        starContainer.style.color = '#3b82f6';
      }
      
      // Toggle favorite on click
      starContainer.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        const favs = JSON.parse(localStorage.getItem('favr_favorites') || '{}');
        if (!favs['fmwvtool-plugins']) favs['fmwvtool-plugins'] = {};
        
        if (favs['fmwvtool-plugins'][pluginId]) {
          delete favs['fmwvtool-plugins'][pluginId];
          starContainer.innerHTML = '☆';
          starContainer.style.opacity = '0.4';
          starContainer.style.color = '';
        } else {
          // Get metadata from plugin registry - match by ID
          const pluginData = pluginRegistry.find(p => p.id === pluginId || p.name === pluginId);
          favs['fmwvtool-plugins'][pluginId] = {
            id: pluginId,
            addedAt: Date.now(),
            metadata: { 
              name: pluginData?.name || pluginId, 
              description: pluginData?.description || '', 
              icon: pluginData ? getPluginIcon(pluginData) : '📦' 
            }
          };
          starContainer.innerHTML = '★';
          starContainer.style.opacity = '1';
          starContainer.style.color = '#3b82f6';
        }
        
        localStorage.setItem('favr_favorites', JSON.stringify(favs));
        window.dispatchEvent(new Event('storage'));
      });
      
      // Insert before shortcut or at end
      const shortcut = button.querySelector('.menu-item__shortcut');
      if (shortcut) {
        button.insertBefore(starContainer, shortcut);
      } else {
        button.appendChild(starContainer);
      }
    });
  }, []);
  
  // Inject stars on mount and when menu changes
  React.useEffect(() => {
    const timer = setTimeout(injectStars, 100);
    
    // Use MutationObserver to detect when menu items are added (sections expanded)
    const observer = new MutationObserver(() => {
      setTimeout(injectStars, 50);
    });
    
    const sideMenu = document.querySelector('.fmwvtool-side-menu');
    if (sideMenu) {
      observer.observe(sideMenu, { childList: true, subtree: true });
    }
    
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [injectStars]);
  
  const handleNavigate = (toolId: string) => {
    console.log('[NavigationSidebar] handleNavigate called with:', toolId);
    setView('tool', { toolId });
    onToolChange(toolId as ToolType);
  };
  
  // Get favorites - memoize to prevent infinite loops
  const favorites = React.useMemo(() => {
    const favs = JSON.parse(localStorage.getItem('favr_favorites') || '{}');
    const favoriteIds = Object.keys(favs['fmwvtool-plugins'] || {});
    return favoriteIds;
  }, [showFavoritesOnly]); // Re-compute when filter changes
  
  // Count favorites per category
  const getFavoritesCountByCategory = (category: string) => {
    return pluginRegistry.filter(p => 
      (p.category || 'utility') === category && favorites.includes(p.id)
    ).length;
  };
  
  // Filter plugins based on favorites
  const filteredPlugins = showFavoritesOnly 
    ? pluginRegistry.filter(p => favorites.includes(p.id))
    : pluginRegistry;
  
  // Group plugins by category
  const pluginsByCategory = filteredPlugins.reduce((acc, plugin) => {
    const category = plugin.category || 'utility';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(plugin);
    return acc;
  }, {} as Record<string, typeof pluginRegistry>);

  // Convert to MenuItem format with categories as submenus
  const menuItems: MenuItemType[] = [
    // Home
    {
      id: 'home',
      type: 'action',
      label: 'Home',
      icon: '🏠',
      action: () => handleNavigate('home'),
      selected: currentTool === 'home'
    },
    
    // Separator
    {
      id: 'sep-home',
      type: 'separator',
      label: ''
    },
    
    // Create submenu for each category
    ...Object.entries(pluginsByCategory).map(([category, plugins]) => {
      const config = CATEGORY_CONFIG[category] || { label: category, icon: '📦' };
      const favCount = getFavoritesCountByCategory(category);
      const enabledCount = plugins.filter(p => p.enabled).length;
      const disabledCount = plugins.filter(p => !p.enabled).length;
      
      // Build badge: Enabled/Disabled ★Favorites
      let badgeText = `${enabledCount}`;
      if (disabledCount > 0) {
        badgeText += `/${disabledCount}`;
      }
      if (favCount > 0) {
        badgeText += ` ★${favCount}`;
      }
      
      return {
        id: `category-${category}`,
        type: 'submenu' as const,
        label: config.label,
        icon: config.icon,
        badge: badgeText,
        expanded: true,
        children: plugins
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(plugin => ({
            id: plugin.id,
            type: 'action' as const,
            label: plugin.name,
            icon: getPluginIcon(plugin),
            tooltip: plugin.description,
            badge: plugin.enabled ? undefined : 'disabled',
            disabled: !plugin.enabled,
            selected: currentTool === plugin.id,
            action: () => handleNavigate(plugin.id),
            className: 'menu-item-with-star',
            metadata: {
              version: plugin.version,
              tags: plugin.tags,
              source: plugin.source,
              pluginId: plugin.id,
              pluginName: plugin.name,
              pluginDescription: plugin.description,
              pluginIcon: getPluginIcon(plugin)
            }
          }))
      };
    }),
    
    // Separator
    {
      id: 'sep-system',
      type: 'separator',
      label: ''
    },
    
    // System items (Settings moved to top nav)
    {
      id: 'dev-panel',
      type: 'action',
      label: 'Dev Panel',
      icon: '🛠️',
      action: () => handleNavigate('dev'),
      selected: currentTool === 'dev'
    }
  ];
  
  const handleMenuItemClick = (item: MenuItemType) => {
    if (item.action) {
      item.action();
    }
  };
  
  return (
    <div className="sidebar-content-wrapper">
      <div className="search-with-star">
        <input
          type="text"
          className="side-menu__search-input"
          placeholder="Search..."
          onChange={(e) => {
            // Handle search filtering
            const searchTerm = e.target.value.toLowerCase();
            const items = document.querySelectorAll('.menu-item__wrapper');
            items.forEach((item) => {
              const label = item.querySelector('.menu-item__label')?.textContent?.toLowerCase();
              if (label && label.includes(searchTerm)) {
                (item as HTMLElement).style.display = '';
              } else if (searchTerm) {
                (item as HTMLElement).style.display = 'none';
              }
            });
          }}
        />
        <button
          className={`favorites-filter-button ${showFavoritesOnly ? 'active' : ''}`}
          onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
          title={showFavoritesOnly ? 'Show all items' : 'Show favorites only'}
        >
          {showFavoritesOnly ? '★' : '☆'}
        </button>
      </div>
      <SideMenu
        appName="fmwvtool"
        items={menuItems}
        theme="dark"
        variant="default"
        collapsible={false}
        defaultCollapsed={false}
        categories={false}
        persistState={true}
        search={false}
        draggable={false}
        onItemClick={handleMenuItemClick}
        className="side-menu"
      />
    </div>
  );
}

// Helper to get appropriate icon for plugin
function getPluginIcon(plugin: typeof pluginRegistry[0]): string {
  // Use category-specific icons
  if (plugin.tags.includes('pdf')) return '📕';
  if (plugin.tags.includes('office')) return '📊';
  if (plugin.tags.includes('table')) return '📋';
  if (plugin.tags.includes('tags')) return '🏷️';
  if (plugin.tags.includes('filemaker')) return '🔗';
  if (plugin.tags.includes('data')) return '🔄';
  if (plugin.tags.includes('picker')) return '🎯';
  if (plugin.tags.includes('files')) return '📁';
  if (plugin.tags.includes('memory')) return '💾';
  if (plugin.tags.includes('scripts')) return '📜';
  
  // Default by category
  const categoryIcons: Record<string, string> = {
    document: '📄',
    data: '🔄',
    ui: '📊',
    integration: '🔗',
    utility: '🛠️',
    examples: '✨',
    'filemaker-scripts': '📜'
  };
  
  return categoryIcons[plugin.category] || '📦';
}
